import { ComponentProps, memo } from 'react';
import Lottie from 'react-lottie';
import { Card, Button, Typography } from 'components';
import AttentionLottie from 'assets/lottie/attention.json';
import H1 from 'components/H/H1';
import { format } from 'utils/format';
import { useTranslation } from 'react-i18next';
import useAdjustIncome from './useAdjustIncome';

const lottieOptions = {
  loop: false,
  autoplay: true,
  animationData: AttentionLottie,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
} satisfies ComponentProps<typeof Lottie>['options'];

export default memo(function AdjustIncome() {
  const { t } = useTranslation();

  const {
    clientBudgetInfo,
    clientBudgetPsd,
    isLoading,
    isPending,
    handleContinue,
    handleUpload,
  } = useAdjustIncome();

  return (
    <Card className="w-full flex flex-col max-w-3xl p-3 mx-auto flex-grow lg:py-4 lg:px-4">
      <div className="w-[75px] mx-auto">
        <Lottie options={lottieOptions} ariaRole="presentation" />
      </div>
      <H1 className="text-2xl lg:text-2xl lg:text-center text-[#464D57]">
        {t('adjust_income:income_discrepancy_title')}
      </H1>
      <div className="text-center border border-[#DDE0EC] bg-[#F3F7FA] rounded-lg p-6 whitespace-pre-line">
        <Typography
          className="text-[15px] leading-[19px]"
          dataTest="description"
        >
          {t('adjust_income:income_verification_message')}
        </Typography>
      </div>

      <div className="w-full flex flex-col mt-6 gap-6">
        <div className="w-full block">
          <ul className="list-none p-0 m-0 border border-[#DDE0EC] rounded-lg">
            <li className="flex justify-between items-center border-b border-[#DDE0EC] py-3 mx-6">
              <div>
                <Typography dataTest="stated-income-label">
                  {t('adjust_income:declared_income')}
                </Typography>
              </div>
              <div>
                <Typography fontWeight={700} dataTest="stated-income-value">
                  {format.number(clientBudgetInfo?.monthlyIncome, 'currency')}
                </Typography>
              </div>
            </li>
            <li className="flex justify-between items-center py-3 mx-6">
              <div>
                <Typography dataTest="verified-income-label">
                  {t('adjust_income:verified_bank_income')}
                </Typography>
              </div>
              <div>
                <Typography fontWeight={700} dataTest="verified-income-value">
                  {format.number(clientBudgetPsd?.monthlyIncome, 'currency')}
                </Typography>
              </div>
            </li>
          </ul>
        </div>
        <h2 className="text-[#464D57] text-base font-bold leading-6">
          {t('adjust_income:please_select_how_to_proceed')}
        </h2>
        <div className="w-full block">
          <ul className="list-none p-0 m-0 border border-[#DDE0EC] rounded-lg">
            <li className="flex justify-between items-center border-b border-[#DDE0EC] py-3 px-6">
              <div>
                <Typography>
                  {t('adjust_income:continue_with_verified_income')} (
                  {format.number(clientBudgetPsd?.monthlyIncome, 'currency')})
                </Typography>
              </div>
              <div>
                <Button
                  onClick={handleContinue}
                  size="m"
                  dataTest="button-continue"
                  buttonClassName="w-40 text-xl"
                  disabled={isLoading || isPending}
                  pending={isPending}
                >
                  {t('adjust_income:continue')}
                </Button>
              </div>
            </li>
            <li className="flex justify-between items-center py-3 px-6">
              <div>
                <Typography>
                  {t('adjust_income:submit_other_proof_of_income')}
                </Typography>
              </div>
              <div>
                <Button
                  buttonClassName="w-40 bg-[#FFA200] enabled:hover:bg-[#FFA200]/80 text-xl"
                  onClick={handleUpload}
                  size="m"
                  dataTest="button-upload"
                  disabled={isLoading || isPending}
                >
                  {t('adjust_income:upload_attachments')}
                </Button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </Card>
  );
});
